import { FC } from 'react';
import classNames from 'classnames';
import { useRouter } from 'next/router';

import { icon_download } from '@gn-audio/icons';
import {
  Button,
  getContrastThemeClass,
  JabraIcon,
  resolveBackgroundStyle,
  useFallbackTranslation,
} from '@gn-audio/jss-components';
import { tailwindConfig } from '@gn-audio/jss-components/tailwind-presets';
import { DEFAULT_LOCALE } from '@gn-audio/jss-core';

import { adjustMEALocales } from '../../common/utils/adjustMEALocaleUtils';
import { DownloadableResourceListItemProps } from './downloadableResourceListTypes';

const isValidSitecoreDate = (date: string | undefined): boolean => {
  return !!date && date !== '0001-01-01T00:00:00Z';
};

export const DownloadableResourceItem: FC<DownloadableResourceListItemProps> = ({ item }) => {
  const router = useRouter();

  const { headline, description, file, fileDate, background, headlineGlobal, descriptionGlobal } =
    item;
  const { t } = useFallbackTranslation();

  const finalHeadline = headline?.value || headlineGlobal?.value;
  const finalDescription = description?.value || descriptionGlobal?.value;

  if (!finalHeadline || !file?.value?.src) {
    return null;
  }

  const itemThemeClass = getContrastThemeClass(background?.value as string);
  const itemBackgroundStyle = resolveBackgroundStyle(background?.value as string) || {
    backgroundColor: tailwindConfig.theme.colors.beige[100],
  };

  const currentLocale = router.locale || DEFAULT_LOCALE;
  const adjustedLocale = adjustMEALocales(currentLocale);
  const formattedDate = isValidSitecoreDate(fileDate?.value)
    ? new Date(fileDate.value).toLocaleDateString(adjustedLocale, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
      })
    : null;

  return (
    <div
      style={itemBackgroundStyle}
      className={classNames(
        'flex flex-col gap-3 rounded-3xl px-6 py-4 text-center md:flex-row md:items-center md:justify-between md:gap-6 md:text-left',
        itemThemeClass
      )}
    >
      <div className="flex flex-col gap-2">
        <h3 className={classNames('heading-5 md:heading-6', !finalDescription && 'mb-0')}>
          {finalHeadline}
        </h3>
        {finalDescription && <p className="paragraph-small">{finalDescription}</p>}
      </div>
      <div className="flex shrink-0 flex-col items-center justify-between gap-4 md:flex-row md:gap-6">
        <span className="text-sm opacity-80">{formattedDate && <span>{formattedDate}</span>}</span>
        <Button
          asLink
          href={file.value.src}
          variant="tertiary"
          size="m"
          download
          target="_blank"
          rel="noopener noreferrer"
          data-wt-name="DownloadButton"
          icon={<JabraIcon Icon={icon_download} />}
          aria-label={`${t('system.Download')} ${file?.value?.extension ? `(${file.value.extension})` : ''} ${finalHeadline || t('system.File')}`}
        >
          {t('system.Download')}
          {file?.value?.extension && (
            <span className="ml-1 uppercase">({file.value.extension})</span>
          )}
        </Button>
      </div>
    </div>
  );
};
